
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shield, AlertTriangle, Eye, UserX } from 'lucide-react';
import { SecurityIncident } from '@/hooks/useContentModeration';
import { toast } from 'sonner';

interface SecurityIncidentsTabProps {
  incidents: SecurityIncident[];
  onUpdateIncident: (id: string, updates: Partial<SecurityIncident>) => Promise<void>;
}

const SecurityIncidentsTab = ({ incidents, onUpdateIncident }: SecurityIncidentsTabProps) => {
  const [selectedIncident, setSelectedIncident] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState<string | null>(null);

  const handleStatusUpdate = async (incidentId: string, status: string, severity?: string) => {
    setProcessing(incidentId);
    try {
      const updates: Partial<SecurityIncident> = { 
        status: status as SecurityIncident['status'],
        ...(severity && { severity: severity as SecurityIncident['severity'] }),
        ...(adminNotes && { admin_notes: adminNotes }),
        ...(status === 'resolved' && { 
          resolved: true, 
          resolved_at: new Date().toISOString() 
        })
      };
      
      await onUpdateIncident(incidentId, updates);
      toast.success('Incident updated successfully');
      setSelectedIncident(null);
      setAdminNotes('');
    } catch (error) {
      toast.error('Failed to update incident');
    } finally {
      setProcessing(null);
    }
  };

  const getSeverityBadge = (severity: string) => {
    const variants = {
      low: 'bg-blue-100 text-blue-800 border-blue-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      critical: 'bg-red-100 text-red-800 border-red-200'
    };
    return variants[severity as keyof typeof variants] || variants.medium;
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      open: 'bg-red-100 text-red-800 border-red-200',
      investigating: 'bg-blue-100 text-blue-800 border-blue-200',
      resolved: 'bg-green-100 text-green-800 border-green-200',
      closed: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return variants[status as keyof typeof variants] || variants.open;
  };

  const activeIncidents = incidents.filter(incident => !incident.resolved);
  const resolvedIncidents = incidents.filter(incident => incident.resolved);

  return (
    <div className="space-y-6">
      {/* Active Security Incidents */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="h-5 w-5 text-red-500" />
            Active Security Incidents ({activeIncidents.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {activeIncidents.length === 0 ? (
            <p className="text-gray-400">No active security incidents</p>
          ) : (
            activeIncidents.map((incident) => (
              <div key={incident.id} className="bg-gray-700 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-400" />
                      <span className="text-white font-medium">{incident.incident_type}</span>
                      <Badge className={getStatusBadge(incident.status)}>
                        {incident.status}
                      </Badge>
                      <Badge className={getSeverityBadge(incident.severity)}>
                        {incident.severity}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-300">
                      {incident.user_id && (
                        <p><strong>User ID:</strong> {incident.user_id}</p>
                      )}
                      {incident.reported_by && (
                        <p><strong>Reported By:</strong> {incident.reported_by}</p>
                      )}
                      {incident.description && (
                        <p><strong>Description:</strong> {incident.description}</p>
                      )}
                      {incident.ip_address && (
                        <p><strong>IP Address:</strong> {incident.ip_address}</p>
                      )}
                      <p><strong>Created:</strong> {new Date(incident.created_at).toLocaleString()}</p>
                      {incident.evidence && (
                        <p><strong>Evidence:</strong> {JSON.stringify(incident.evidence)}</p>
                      )}
                    </div>
                  </div>
                </div>

                {selectedIncident === incident.id && (
                  <div className="space-y-3 border-t border-gray-600 pt-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Select onValueChange={(value) => handleStatusUpdate(incident.id, value)}>
                        <SelectTrigger className="bg-gray-600 border-gray-500">
                          <SelectValue placeholder="Update Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="investigating">Start Investigation</SelectItem>
                          <SelectItem value="resolved">Mark Resolved</SelectItem>
                          <SelectItem value="closed">Close Incident</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <Select onValueChange={(value) => handleStatusUpdate(incident.id, incident.status, value)}>
                        <SelectTrigger className="bg-gray-600 border-gray-500">
                          <SelectValue placeholder="Update Severity" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <Textarea
                      placeholder="Add admin notes"
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      className="bg-gray-600 border-gray-500 text-white"
                    />
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleStatusUpdate(incident.id, 'resolved')}
                        disabled={processing === incident.id}
                        className="bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        <Shield className="h-4 w-4 mr-1" />
                        Resolve
                      </Button>
                      <Button
                        onClick={() => handleStatusUpdate(incident.id, 'investigating')}
                        disabled={processing === incident.id}
                        className="bg-blue-600 hover:bg-blue-700"
                        size="sm"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Investigate
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedIncident(null);
                          setAdminNotes('');
                        }}
                        variant="outline"
                        size="sm"
                        className="border-gray-600 text-gray-300"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {selectedIncident !== incident.id && (
                  <Button
                    onClick={() => setSelectedIncident(incident.id)}
                    variant="outline"
                    size="sm"
                    className="border-gray-600 text-gray-300 hover:bg-gray-600"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Manage Incident
                  </Button>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Resolved Incidents */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Recent Resolved Incidents</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {resolvedIncidents.slice(0, 10).map((incident) => (
            <div key={incident.id} className="bg-gray-700 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm">{incident.incident_type}</span>
                    <Badge className={getStatusBadge(incident.status)}>
                      {incident.status}
                    </Badge>
                    <Badge className={getSeverityBadge(incident.severity)}>
                      {incident.severity}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-400">
                    {incident.user_id && `User: ${incident.user_id} • `}
                    {new Date(incident.created_at).toLocaleDateString()}
                  </p>
                  {incident.admin_notes && (
                    <p className="text-xs text-gray-300">Notes: {incident.admin_notes}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default SecurityIncidentsTab;
